import { GetQueryVariables } from "api/apiFunctions";
import { getRequest } from "api/commonActions/get";
import { postRequest } from "api/commonActions/post";
import { AxiosResponse } from "axios";
import { Requests } from "config/api";
import {
  DeleteMessageProps,
  DeleteMessageReturnType,
  GetConversationProps,
  GetConversationReturnType,
  GetConversationsProps,
  GetConversationsReturnType,
  GetMessagesProps,
  GetMessagesReturnType,
  MarkConversationReadProps,
  MarkConversationReadReturnType,
  SendMessageProps,
  SendMessageReturnType,
  SendDirectMessageProps,
  SendDirectMessageReturnType,
} from "declarations/chatServices";
import { QueryParamsProps } from "declarations/commonServices";
import { patchRequest } from "api/commonActions/update";
import { deleteRequest } from "api/commonActions/delete";

export const GetConversations = async ({
  limit,
  offset,
  search,
  setLoading,
}: GetConversationsProps): Promise<GetConversationsReturnType> => {
  let queryParams: QueryParamsProps = GetQueryVariables({
    limit,
    offset,
    search,
  });

  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await getRequest({
      route: Requests.get.getConversations,
      queryParams,
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const conversations = response.data?.conversations;
    const count = response.data?.count;
    const message = response?.data?.msg;

    return {
      status: status,
      message: message,
      conversations,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const GetConversation = async ({
  id,
  setLoading,
}: GetConversationProps): Promise<GetConversationReturnType> => {
  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await getRequest({
      route: Requests.get.getConversation,
      pathVariables: { id },
    });
    setLoading && setLoading(false);

    return {
      status: response?.status,
      message: response?.data?.msg,
      conversation: response?.data?.conversation || null,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const SendMessage = async ({
  text,
  car,
  carId,
  carTitle,
  carUrl,
  setLoading,
  id,
  clientMessageId,
}: SendMessageProps): Promise<SendMessageReturnType> => {
  let payload: SendMessageProps = {
    text: text,
    id,
  };

  if (car) {
    payload.car = car;
  }
  if (carId) payload.carId = carId;
  if (carTitle) payload.carTitle = carTitle;
  if (carUrl) payload.carUrl = carUrl;

  if (clientMessageId) {
    payload.clientMessageId = clientMessageId;
  }

  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await postRequest({
      route: Requests.post.sendMessage,
      pathVariables: { id },
      payload,
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const text = response.data?.message;
    const conversation = response.data?.conversation;
    const message = response?.data?.msg;

    return {
      status: status,
      newMessage: text,
      conversation,
      message,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const SendDirectMessage = async ({
  customerId,
  text,
  car,
  carId,
  carTitle,
  carUrl,
  setLoading,
}: SendDirectMessageProps): Promise<SendDirectMessageReturnType> => {
  let payload: SendDirectMessageProps = {
    customerId,
    text,
  };

  if (car) {
    payload.car = car;
  }
  if (carId) payload.carId = carId;
  if (carTitle) payload.carTitle = carTitle;
  if (carUrl) payload.carUrl = carUrl;

  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await postRequest({
      route: Requests.post.sendDirectMessage,
      pathVariables: { customerId },
      payload,
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const newMessage = response.data?.message;
    const conversation = response.data?.conversation;
    const message = response?.data?.msg;

    return {
      status,
      newMessage,
      conversation,
      message,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const MarkConversationRead = async ({
  id,
  setLoading,
}: MarkConversationReadProps): Promise<MarkConversationReadReturnType> => {
  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await patchRequest({
      route: Requests.update.readConversation,
      pathVariables: { id },
      payload: {},
    });
    setLoading && setLoading(false);

    return {
      status: response?.status,
      message: response?.data?.msg,
      conversation: response?.data?.conversation || null,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const GetMessages = async ({
  limit,
  offset,
  setLoading,
  id,
}: GetMessagesProps): Promise<GetMessagesReturnType> => {
  let queryParams: QueryParamsProps = GetQueryVariables({
    limit,
    offset,
  });

  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await getRequest({
      route: Requests.get.getMessages,
      queryParams,
      pathVariables: { id },
    });
    setLoading && setLoading(false);

    const status = response?.status;
    const messages = response.data?.messages;
    const count = response.data?.count;
    const message = response?.data?.msg;

    return {
      status: status,
      message: message,
      messages,
      count,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};

export const DeleteMessage = async ({
  id,
  setLoading,
}: DeleteMessageProps): Promise<DeleteMessageReturnType> => {
  try {
    setLoading && setLoading(true);
    const response: AxiosResponse = await deleteRequest({
      route: Requests.delete.deleteMessage,
      pathVariables: { id },
    });
    setLoading && setLoading(false);

    return {
      status: response?.status,
      message: response?.data?.msg,
      deletedMessageId: response?.data?.deletedMessageId,
      conversation: response?.data?.conversation || null,
    };
  } catch (error) {
    setLoading && setLoading(false);
    throw error;
  }
};
